package com.arena_web.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.theme.withAlpha
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*

val ShimmerKeyframes = Keyframes {
    from {
        Modifier.styleModifier { property("background-position", "-200% 0") }
    }
    to {
        Modifier.styleModifier { property("background-position", "200% 0") }
    }
}

@Composable
fun SkeletonBox(
    modifier: Modifier = Modifier,
    width: CSSLengthOrPercentageNumericValue = 100.percent,
    height: CSSLengthOrPercentageNumericValue = 20.px,
    borderRadius: CSSLengthOrPercentageNumericValue = 8.px
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)
    val baseColor = colors.surfaceVariant.toRgbString()
    val highlightColor = colors.border.toRgbString()

    Box(
        modifier = modifier
            .width(width)
            .height(height)
            .borderRadius(borderRadius)
            .animation(
                ShimmerKeyframes.toAnimation(
                    duration = 1.5.s,
                    timingFunction = AnimationTimingFunction.Linear
                )
            )
            .styleModifier {
                property("animation-iteration-count", "infinite")
                property("background", "linear-gradient(90deg, $baseColor 25%, $highlightColor 50%, $baseColor 75%)")
                property("background-size", "200% 100%")
            }
    )
}

@Composable
fun SkeletonText(
    modifier: Modifier = Modifier,
    lines: Int = 3,
    gap: CSSLengthOrPercentageNumericValue = 8.px
) {
    Column(modifier = modifier.fillMaxWidth().gap(gap)) {
        repeat(lines) { index ->
            SkeletonBox(
                width = if (index == lines - 1) 60.percent else 100.percent,
                height = 14.px,
                borderRadius = 4.px
            )
        }
    }
}

@Composable
fun SkeletonCard(
    modifier: Modifier = Modifier,
    imageHeight: CSSLengthOrPercentageNumericValue = 200.px
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    Column(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(colors.surface)
            .borderRadius(12.px)
            .padding(16.px)
            .gap(12.px)
            .styleModifier {
                property("overflow", "hidden")
            }
    ) {
        SkeletonBox(
            height = imageHeight,
            borderRadius = 8.px
        )
        SkeletonBox(height = 20.px, width = 70.percent)
        SkeletonText(lines = 2)
        Row(Modifier.gap(8.px)) {
            SkeletonBox(width = 80.px, height = 32.px, borderRadius = 16.px)
            SkeletonBox(width = 80.px, height = 32.px, borderRadius = 16.px)
        }
    }
}

@Composable
fun SkeletonAvatar(
    modifier: Modifier = Modifier,
    size: CSSLengthOrPercentageNumericValue = 48.px
) {
    SkeletonBox(
        modifier = modifier,
        width = size,
        height = size,
        borderRadius = 50.percent
    )
}
